import { useCallback, useMemo } from 'react';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { ProjectServiceConnector } from '@/modules/api/ProjectApiConnector';
import { useAppMetaStore } from '@/modules/floorplanV2/store/project/projectState';
import { useVersioning } from '@/modules/floorplanV2/hooks';
import { useVersioningStore } from '../store';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { HistoryServiceFactory } from '@/modules/canvas/services/history/HistoryServiceFactory';
import { logger } from '@common/logging/logger';

export const useVersioningCallbacks = () => {
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const historySvc = useMemo(() => HistoryServiceFactory.getInstance().createHistoryService(), []);
  const { loadVersion } = useVersioning();

  const loadAll = useCallback(async (skipLoader: boolean) => {
    const { setVersions, setLoadingStatus } = useVersioningStore.getState();

    if (!skipLoader) {
      setLoadingStatus('loading');
    }

    try {
      const { activeProject, activeLayoutAlternative } = useAppMetaStore.getState();
      const { revisionId: currentLoadedRevisionId } = useFloorplanDesignStore.getState().metaData;

      if (activeProject.status !== 'loaded') {
        logger.error('Expected activeProject to be laoded');
        return;
      }
      if (activeLayoutAlternative.status !== 'loaded') {
        logger.error('Expected layoutAlternative to be laoded');
        return;
      }

      const allRevisionMetaDatas =
        await ProjectServiceConnector.getInstance().getFloorplanRevisionMetaDatas({
          projectId: activeProject.data.id,
          alternativeId: activeLayoutAlternative.data.alternativeId,
        });

      const currentRevision = allRevisionMetaDatas.find(
        (item) => item.metaData.revisionId === currentLoadedRevisionId,
      );

      if (!currentRevision) {
        logger.error('Expected currentRevision to be defined');
        return;
      }

      setVersions(
        allRevisionMetaDatas.map((item) => ({
          id: item.metaData.revisionId,
          version: item.metaData.version,
          updatedAt: new Date(item.metaData.lastUpdated),
        })),
      );
    } catch (error) {
      setVersions([]);
      logger.error(error);
      throw error;
    } finally {
      setLoadingStatus('idle');
    }
  }, []);

  const changeVersion = useCallback(
    async (id: string) => {
      const { activeLayoutAlternative } = useAppMetaStore.getState();
      if (activeLayoutAlternative.status !== 'loaded') {
        logger.error('Expected activeLayoutAlternative to be loaded. ');
        return;
      }

      appSvc.showLoadContainer('TRANSPARENT');

      await loadVersion(id);

      historySvc.clearHistory();
      appSvc.hideLoadContainer();
    },
    [appSvc, loadVersion, historySvc],
  );

  return {
    loadAll,
    changeVersion,
  };
};
